/**
 * Laravel reports 422s as `{ errors: { field: [message, ...] } }`. Both helpers
 * read that shape; the classified `kind` comes from the axios interceptor.
 */
export interface ApiFailure {
  kind?: string
  response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } }
}

/** The first message the server returned for one field, if it named that field. */
export function fieldErrorFor(failure: ApiFailure, field: string): string | undefined {
  return failure.response?.data?.errors?.[field]?.[0]
}

/**
 * The 422 messages that belong under an input, keyed by field.
 *
 * Only the fields the form actually renders are picked up. A 422 naming anything
 * else — a dead reset token, an expired challenge — has no input to sit under,
 * so returning `null` lets the caller fall through to the form-level surface
 * rather than swallow it.
 */
export function fieldErrorsFrom(
  failure: ApiFailure,
  fields: readonly string[],
): Record<string, string> | null {
  if (!failure.response?.data?.errors) return null

  const matched = Object.fromEntries(
    fields.flatMap((field) => {
      const message = fieldErrorFor(failure, field)
      return message === undefined ? [] : [[field, message]]
    }),
  )

  return Object.keys(matched).length > 0 ? matched : null
}

/** EMR's `HasApiResponse::internalServerError` answers `{ errors: [{ message }] }`. */
export function listedErrorMessage(data: unknown): string | undefined {
  const errors = (data as { errors?: unknown } | null | undefined)?.errors
  const message = Array.isArray(errors)
    ? (errors[0] as { message?: unknown } | undefined)?.message
    : undefined
  return typeof message === 'string' && message ? message : undefined
}

function ticketErrorMessage(data: unknown): string | undefined {
  const message = (data as { error?: { message?: unknown } } | null | undefined)?.error?.message
  return typeof message === 'string' && message ? message : undefined
}

/** The reason a failure body gives: Laravel's top-level `message`, else EMR's listed one, else the ticket service's. */
export function failureMessage(data: unknown): string | undefined {
  const message = (data as { message?: unknown } | null | undefined)?.message
  return typeof message === 'string' && message
    ? message
    : (listedErrorMessage(data) ?? ticketErrorMessage(data))
}
